import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Result } from '../models/result';
import useSchoolStore from './schoolStore';
import useUserStore from './userStore';
import useAuthStore from './authStore';
import * as feeRepo from '../repositories/fee/feeRepo';
import * as privilegeRepo from '../repositories/privilegeRepo';
import { Privileges } from '../repositories/privilegeRepo';
import * as utils from '../utils/utils';
import * as feeUtils from '../utils/feeUtils';
import * as studentRepo from '../repositories/user/studentRepo';

export const useFeeStore = create(persist((set, get) => ({
    // State
    onlinePayments: null,
    feeReceipts: null,
    feeDues: null,
    
    // Loading states
    isOnlinePaymentsLoading: false,
    isFeeReceiptsLoading: false,
    isFeeDuesLoading: false,
    
    // Error states
    onlinePaymentsError: null,
    feeReceiptsError: null,
    feeDuesError: null,

    // General error
    error: null,

    // Upload states
    isUploading: false,
    uploadError: null,
    uploadSuccess: false,
    uploadMessage: null,
    uploadWarnings: null,

    /**
     * Gets online payments for the current school
     */
    getOnlinePayments: async (limitCount = null) => {
        const { currentUser } = useUserStore.getState();
        const { schoolData } = useSchoolStore.getState();

        if (!currentUser || !schoolData) {
            set({ onlinePaymentsError: 'Failed to get user or school info' });
            return new Result({ success: false, message: 'Failed to get user or school info' });
        }

        // Check privilege
        set({ isOnlinePaymentsLoading: true, onlinePaymentsError: null });
        if (!utils.isAdminUser(currentUser.type) && !utils.isOfficeUser(currentUser.type)) {
            const result = await privilegeRepo.hasPrivilege({
                schoolId: schoolData.id,
                privilege: Privileges.FEE_PRIVILEGE || 'FEE_MANAGEMENT', // Fallback privilege name
                userName: currentUser.name,
            });

            if (!result.success) {
                set({ 
                    onlinePaymentsError: result.message, 
                    isOnlinePaymentsLoading: false 
                });
                return result;
            }
        }

        const paymentsResult = await feeRepo.getOnlinePayments(schoolData.id, limitCount);

        if (!paymentsResult.success) {
            set({ 
                onlinePaymentsError: paymentsResult.message, 
                isOnlinePaymentsLoading: false 
            });
            return paymentsResult;
        }

        set({ 
            onlinePayments: paymentsResult.value, 
            isOnlinePaymentsLoading: false,
            onlinePaymentsError: null
        });
        return paymentsResult;
    },

    /**
     * Gets fee receipts for the current school and academic year
     */
    getFeeReceipts: async (academicYear = null, limitCount = null) => {
        const { currentUser } = useUserStore.getState();
        const { schoolData } = useSchoolStore.getState();

        if (!currentUser || !schoolData) {
            set({ feeReceiptsError: 'Failed to get user or school info' });
            return new Result({ message: 'Failed to get user or school info' });
        }

        // Use current academic year if not provided
        const targetAcademicYear = academicYear || schoolData.currentAcademicYear;
        if (!targetAcademicYear) {
            set({ feeReceiptsError: 'Academic year not found' });
            return new Result({ message: 'Academic year not found' });
        }

        // Check privilege
        set({ isFeeReceiptsLoading: true, feeReceiptsError: null });
        if (!utils.isAdminUser(currentUser.type) && !utils.isOfficeUser(currentUser.type)) {
            const result = await privilegeRepo.hasPrivilege({
                schoolId: schoolData.id,
                privilege: Privileges.FEE_PRIVILEGE || 'FEE_MANAGEMENT', // Fallback privilege name
                userName: currentUser.name,
            });

            if (!result.success) {
                set({ 
                    feeReceiptsError: result.message, 
                    isFeeReceiptsLoading: false 
                });
                return result;
            }
        }

        const receiptsResult = await feeRepo.getFeeReceipts(schoolData.id, targetAcademicYear, limitCount);

        if (!receiptsResult.success) {
            set({ 
                feeReceiptsError: receiptsResult.message, 
                isFeeReceiptsLoading: false 
            });
            return receiptsResult;
        }

        set({ 
            feeReceipts: receiptsResult.value, 
            isFeeReceiptsLoading: false,
            feeReceiptsError: null
        });
        return receiptsResult;
    },

    /**
     * Gets fee dues for the current school
     */
    getFeeDues: async (limitCount = null) => {
        const { currentUser } = useUserStore.getState();
        const { schoolData } = useSchoolStore.getState();

        if (!currentUser || !schoolData) {
            set({ feeDuesError: 'Failed to get user or school info' });
            return new Result({ message: 'Failed to get user or school info' });
        }

        // Check privilege
        set({ isFeeDuesLoading: true, feeDuesError: null });
        if (!utils.isAdminUser(currentUser.type) && !utils.isOfficeUser(currentUser.type)) {
            const result = await privilegeRepo.hasPrivilege({
                schoolId: schoolData.id,
                privilege: Privileges.FEE_PRIVILEGE || 'FEE_MANAGEMENT', // Fallback privilege name
                userName: currentUser.name,
            });

            if (!result.success) {
                set({ 
                    feeDuesError: result.message, 
                    isFeeDuesLoading: false 
                });
                return result;
            }
        }

        const duesResult = await feeRepo.getFeeDue(schoolData.id, limitCount);

        if (!duesResult.success) {
            set({ 
                feeDuesError: duesResult.message, 
                isFeeDuesLoading: false 
            });
            return duesResult;
        }

        set({ 
            feeDues: duesResult.value, 
            isFeeDuesLoading: false,
            feeDuesError: null
        });
        return duesResult;
    },

    /**
     * Gets payments by admission number
     */
    getPaymentsByAdmission: async (admissionNo) => {
        const { schoolData } = useSchoolStore.getState();

        if (!schoolData || !admissionNo) {
            return new Result({ success: false, message: 'School data and admission number are required' });
        }

        return await feeRepo.getOnlinePaymentsByAdmission(schoolData.id, admissionNo);
    },

    /**
     * Gets dues by admission number
     */
    getDuesByAdmission: async (admissionNo) => {
        const { schoolData } = useSchoolStore.getState();

        if (!schoolData || !admissionNo) {
            return new Result({ success: false, message: 'School data and admission number are required' });
        }

        return await feeRepo.getFeeDuesByAdmission(schoolData.id, admissionNo);
    },

    /**
     * Refreshes all fee data
     */
    refreshAllData: async () => {
        const promises = [
            get().getOnlinePayments(),
            get().getFeeReceipts(),
            get().getFeeDues()
        ];

        const results = await Promise.allSettled(promises);
        
        // Check if any failed
        const failed = results.filter(result => result.status === 'rejected' || !result.value?.success);
        if (failed.length > 0) {
            console.warn('Some fee data refresh operations failed:', failed);
        }

        return new Result({ 
            success: failed.length === 0, 
            message: failed.length === 0 ? 'All data refreshed successfully' : 'Some data failed to refresh'
        });
    },

    /**
     * Clears all fee data
     */
    clearFeeData: () => {
        set({
            onlinePayments: null,
            feeReceipts: null,
            feeDues: null,
            onlinePaymentsError: null,
            feeReceiptsError: null,
            feeDuesError: null,
            error: null
        });
    },

    /**
     * Clears upload state
     */
    clearUploadState: () => {
        set({
            isUploading: false,
            uploadError: null,
            uploadSuccess: false,
            uploadMessage: null,
            uploadWarnings: null
        });
    },

    /**
     * Gets summary statistics
     */
    getSummaryStats: () => {
        const { onlinePayments, feeReceipts, feeDues } = get();

        const stats = {
            totalPayments: onlinePayments?.length || 0,
            totalReceipts: feeReceipts?.length || 0,
            totalDues: feeDues?.length || 0,
            totalPaymentAmount: 0,
            totalReceiptAmount: 0,
            totalDueAmount: 0,
            overdueCount: 0
        };

        // Calculate payment amounts
        if (onlinePayments) {
            stats.totalPaymentAmount = onlinePayments.reduce((sum, payment) => sum + payment.amount, 0);
        }

        // Calculate receipt amounts
        if (feeReceipts) {
            stats.totalReceiptAmount = feeReceipts.reduce((sum, receipt) => sum + receipt.totalPaid, 0);
        }

        // Calculate due amounts and overdue count
        if (feeDues) {
            stats.totalDueAmount = feeDues.reduce((sum, due) => sum + due.amount, 0);
            stats.overdueCount = feeDues.filter(due => due.isOverdue()).length;
        }

        return stats;
    },

    /**
     * Uploads fee receipts from CSV data
     * @param {string} csvContent - CSV file content
     */
    uploadFeeReceipts: async (csvContent) => {
        const { currentUser } = useUserStore.getState();
        const { schoolData } = useSchoolStore.getState();

        // Clear previous upload state
        set({
            isUploading: true,
            uploadError: null,
            uploadSuccess: false,
            uploadMessage: null
        });

        if (!currentUser || !schoolData) {
            set({
                isUploading: false,
                uploadError: 'Failed to get user or school info',
                uploadSuccess: false
            });
            return;
        }

        // Check privilege
        if (!utils.isAdminUser(currentUser.type) && !utils.isOfficeUser(currentUser.type)) {
            const result = await privilegeRepo.hasPrivilege({
                schoolId: schoolData.id,
                privilege: Privileges.FEE_PRIVILEGE || 'FEE_MANAGEMENT',
                userName: currentUser.name,
            });

            if (!result.success) {
                set({
                    isUploading: false,
                    uploadError: result.message,
                    uploadSuccess: false
                });
                return;
            }
        }

        try {
            // Parse CSV content
            const csvRows = utils.parseCSVContent(csvContent);
            
            if (!csvRows || csvRows.length === 0) {
                set({
                    isUploading: false,
                    uploadError: 'CSV file is empty or invalid format',
                    uploadSuccess: false
                });
                return;
            }

            // Extract admission numbers and validate
            const processedRows = [];
            const invalidRows = [];

            csvRows.forEach((row, index) => {
                const admissionNo = feeUtils.extractAdmissionNumber(row);
                const voucherNo = feeUtils.extractVoucherNo(row);
                
                if (!admissionNo) {
                    invalidRows.push({
                        rowIndex: index + 2, // +2 because index starts at 0 and we skip header
                        error: 'Missing admission number',
                        data: row
                    });
                } else if (!voucherNo) {
                    invalidRows.push({
                        rowIndex: index + 2,
                        error: 'Missing voucher/bill number',
                        data: row
                    });
                } else {
                    processedRows.push({
                        admissionNo,
                        voucherNo,
                        originalData: row,
                        rowIndex: index + 2
                    });
                }
            });

            // If no valid rows found
            if (processedRows.length === 0) {
                set({
                    isUploading: false,
                    uploadError: 'No valid admission numbers found in CSV',
                    uploadSuccess: false
                });
                return;
            }

            // Extract unique admission numbers
            const admissionNumbers = [...new Set(processedRows.map(row => row.admissionNo))];

            // Get students by admission numbers
            const studentsResult = await studentRepo.getStudentsByAdmissionNos(schoolData.id, admissionNumbers);

            if (!studentsResult.success) {
                set({
                    isUploading: false,
                    uploadError: `Failed to validate students: ${studentsResult.message}`,
                    uploadSuccess: false
                });
                return;
            }

            const { valid: validStudents, invalid: invalidStudents } = studentsResult.value;
            
            // Create maps for quick lookup
            const validStudentMap = new Map();
            validStudents.forEach(student => {
                validStudentMap.set(student.admissionNo, student);
            });

            const invalidStudentMap = new Map();
            invalidStudents.forEach(invalid => {
                invalidStudentMap.set(invalid.admissionNo, invalid.error);
            });

            // Process rows and categorize as valid or invalid
            const validReceipts = [];
            const finalInvalidRows = [...invalidRows]; // Start with rows that had missing admission numbers/voucher numbers

            processedRows.forEach(row => {
                if (validStudentMap.has(row.admissionNo)) {
                    const student = validStudentMap.get(row.admissionNo);
                    validReceipts.push({
                        admissionNo: row.admissionNo,
                        student: student,
                        receiptData: row.originalData,
                        rowIndex: row.rowIndex
                    });
                } else {
                    const error = invalidStudentMap.get(row.admissionNo) || 'Student not found';
                    finalInvalidRows.push({
                        rowIndex: row.rowIndex,
                        admissionNo: row.admissionNo,
                        error: error,
                        data: row.originalData
                    });
                }
            });

            // Return error if there are no valid rows to upload
            if (validReceipts.length === 0) {
                const errorLines = finalInvalidRows.map(invalid => 
                    `Row ${invalid.rowIndex}: ${invalid.error}${invalid.admissionNo ? ` (Admission No: ${invalid.admissionNo})` : ''}`
                );
                
                const detailedMessage = [
                    'No valid rows found to upload.',
                    `Errors found in all ${finalInvalidRows.length} rows:`,
                    '',
                    ...errorLines
                ].join('\n');

                set({
                    isUploading: false,
                    uploadError: detailedMessage,
                    uploadSuccess: false,
                    uploadWarnings: null
                });
                return;
            }

            // Convert valid receipts to fee receipt format for upload
            const feeReceiptsToUpload = validReceipts.map(item => {
                const receiptData = item.receiptData;
                const student = item.student;
                
                // Convert receiptData map object to array of key-value pairs
                const receiptDataArray = Object.entries(receiptData).map(([key, value]) => ({
                    [key]: value
                }));
                
                // Create FeeReceipt object from CSV row data
                return {
                    Class: student.Class,
                    section: student.sec,
                    academicYear: schoolData.currentAcademicYear,
                    admissionNo: item.admissionNo,
                    data: receiptDataArray, // Store as array of key-value pairs
                    userId: student.userId,
                    voucherNo: feeUtils.extractVoucherNo(receiptData)
                };
            });

            // Upload the fee receipts using the repository function
            console.log(`uploading fee receipts`)
            const uploadResult = await feeRepo.uploadFeeReceipts(
                schoolData.id,
                schoolData.currentAcademicYear,
                feeReceiptsToUpload
            );

            if (!uploadResult.success) {
                set({
                    isUploading: false,
                    uploadError: `Failed to upload fee receipts: ${uploadResult.message}`,
                    uploadSuccess: false,
                    uploadWarnings: null
                });
                return;
            }

            // Success! Update state and refresh data
            set({
                isUploading: false,
                uploadError: null,
                uploadSuccess: true,
                uploadMessage: finalInvalidRows.length > 0
                    ? `Uploaded ${validReceipts.length} records successfully. ${finalInvalidRows.length} records failed validation and were skipped.`
                    : (uploadResult.value || `Successfully uploaded ${validReceipts.length} fee receipts`),
                uploadWarnings: finalInvalidRows.length > 0 ? finalInvalidRows : null
            });

            // Refresh fee receipts data
             // Refresh the Fee Receipts data after a few seconds. This is because the [totalPaid] is populated
              // from the server.
            setTimeout(() => {
                get().getFeeReceipts();
            }, 4000);

        } catch (error) {
            console.error('Error in uploadFeeReceipts:', error);
            set({
                isUploading: false,
                uploadError: `Error processing CSV: ${error.message}`,
                uploadSuccess: false
            });
        }
    },

    /**
     * Uploads fee dues from CSV data
     * @param {string} csvContent - CSV file content
     * @param {string} month - Month for which dues are being uploaded
     */
    uploadFeeDues: async (csvContent, month) => {
        const { currentUser } = useUserStore.getState();
        const { schoolData } = useSchoolStore.getState();

        // Clear previous upload state
        set({
            isUploading: true,
            uploadError: null,
            uploadSuccess: false,
            uploadMessage: null
        });

        if (!currentUser || !schoolData) {
            set({
                isUploading: false,
                uploadError: 'Failed to get user or school info',
                uploadSuccess: false
            });
            return;
        }

        if (!month) {
            set({
                isUploading: false,
                uploadError: 'Month parameter is required',
                uploadSuccess: false
            });
            return;
        }

        // Check privilege
        if (!utils.isAdminUser(currentUser.type) && !utils.isOfficeUser(currentUser.type)) {
            const result = await privilegeRepo.hasPrivilege({
                schoolId: schoolData.id,
                privilege: Privileges.FEE_PRIVILEGE || 'FEE_MANAGEMENT',
                userName: currentUser.name,
            });

            if (!result.success) {
                set({
                    isUploading: false,
                    uploadError: result.message,
                    uploadSuccess: false
                });
                return;
            }
        }

        try {
            // Parse CSV content
            const csvRows = utils.parseCSVContent(csvContent);
            
            if (!csvRows || csvRows.length === 0) {
                set({
                    isUploading: false,
                    uploadError: 'CSV file is empty or invalid format',
                    uploadSuccess: false
                });
                return;
            }

            // Validate CSV content for required fields
            const processedRows = [];
            const invalidRows = [];

            csvRows.forEach((row, index) => {
                const admissionNo = feeUtils.extractAdmissionNumber(row);
                const amount = feeUtils.extractAmount(row);
                
                if (!admissionNo) {
                    invalidRows.push({
                        rowIndex: index + 2, // +2 because index starts at 0 and we skip header
                        error: 'Missing admission number',
                        data: row
                    });
                    
                } else if (!amount || isNaN(parseFloat(amount))) {
                    invalidRows.push({
                        rowIndex: index + 2,
                        error: 'Missing or invalid amount',
                        data: row
                    });
                } else {
                    processedRows.push({
                        admissionNo,
                        amount: parseFloat(amount),
                        originalData: row,
                        rowIndex: index + 2
                    });
                }
            });

            // If no valid rows found
            if (processedRows.length === 0) {
                let errorMessage = 'No valid rows found with admission number and amount'
                if (invalidRows.length > 0) {
                 errorMessage = invalidRows.map(row => `Row ${row.rowIndex}: ${row.error}`).join('\n')   
                }
                set({
                    isUploading: false,
                    uploadError: errorMessage,
                    uploadSuccess: false
                });
                return;
            }

            // Extract unique admission numbers for student validation
            const admissionNumbers = [...new Set(processedRows.map(row => row.admissionNo))];

            // Get students by admission numbers to validate they exist
            const studentsResult = await studentRepo.getStudentsByAdmissionNos(schoolData.id, admissionNumbers);

            if (!studentsResult.success) {
                set({
                    isUploading: false,
                    uploadError: `Failed to validate students: ${studentsResult.message}`,
                    uploadSuccess: false
                });
                return;
            }

            const { valid: validStudents, invalid: invalidStudents } = studentsResult.value;
            
            // Create map for quick lookup of valid students
            const validStudentMap = new Map();
            validStudents.forEach(student => {
                validStudentMap.set(student.admissionNo, student);
            });

            const invalidStudentMap = new Map();
            invalidStudents.forEach(invalid => {
                invalidStudentMap.set(invalid.admissionNo, invalid.error);
            });

            // Process rows and categorize as valid or invalid
            const validDues = [];
            const finalInvalidRows = [...invalidRows]; // Start with rows that had missing fields

            processedRows.forEach(row => {
                if (validStudentMap.has(row.admissionNo)) {
                    validDues.push({
                        admissionNo: row.admissionNo,
                        amount: row.amount,
                        month: month, // Set the month parameter
                        dueParticulars: feeUtils.extractDueParticulars(row.originalData) || '',
                        rowIndex: row.rowIndex
                    });
                } else {
                    const error = invalidStudentMap.get(row.admissionNo) || 'Student not found';
                    finalInvalidRows.push({
                        rowIndex: row.rowIndex,
                        admissionNo: row.admissionNo,
                        error: error,
                        data: row.originalData
                    });
                }
            });

            // Return error if there are no valid rows to upload
            if (validDues.length === 0) {
                const errorLines = finalInvalidRows.map(invalid => 
                    `Row ${invalid.rowIndex}: ${invalid.error}${invalid.admissionNo ? ` (Admission No: ${invalid.admissionNo})` : ''}`
                );
                
                const detailedMessage = [
                    'No valid rows found to upload.',
                    `Errors found in all ${finalInvalidRows.length} rows:`,
                    '',
                    ...errorLines
                ].join('\n');

                set({
                    isUploading: false,
                    uploadError: detailedMessage,
                    uploadSuccess: false,
                    uploadWarnings: null
                });
                return;
            }

            // Upload the fee dues using the repository function
            const uploadResult = await feeRepo.uploadFeeDues(schoolData.id, validDues);

            if (!uploadResult.success) {
                set({
                    isUploading: false,
                    uploadError: `Failed to upload fee dues: ${uploadResult.message}`,
                    uploadSuccess: false,
                    uploadWarnings: null
                });
                return;
            }

            // Success! Update state and refresh data
            set({
                isUploading: false,
                uploadError: null,
                uploadSuccess: true,
                uploadMessage: finalInvalidRows.length > 0
                    ? `Uploaded ${validDues.length} records successfully. ${finalInvalidRows.length} records failed validation and were skipped.`
                    : (uploadResult.value || `Successfully uploaded ${validDues.length} fee dues for ${month}`),
                uploadWarnings: finalInvalidRows.length > 0 ? finalInvalidRows : null
            });

            // Refresh fee dues data
            get().getFeeDues();

        } catch (error) {
            console.error('Error in uploadFeeDues:', error);
            set({
                isUploading: false,
                uploadError: `Error processing CSV: ${error.message}`,
                uploadSuccess: false
            });
        }
    }
}),
{
    name: 'feeStore',
    partialize: (state) => ({
        // Don't persist any data for now, always fetch fresh
    })
}
));

export default useFeeStore;
